import React, { useState } from 'react';
import { X, CreditCard, Wallet, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = {
  start: { name: "START", price: 8000, currency: "MXN", period: "/año" },
  pro: { name: "PRO", price: 12000, currency: "MXN", period: "/año" },
  premium: { name: "PREMIUM", price: 16000, currency: "MXN", period: "/año" }
};

const POPULAR_CRYPTOS = [
  { id: 'btc', name: 'Bitcoin', symbol: 'BTC' },
  { id: 'eth', name: 'Ethereum', symbol: 'ETH' },
  { id: 'usdt', name: 'Tether', symbol: 'USDT' },
  { id: 'usdc', name: 'USD Coin', symbol: 'USDC' },
  { id: 'sol', name: 'Solana', symbol: 'SOL' }
];

export default function PaymentModal({ planId, onClose }) {
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCrypto, setSelectedCrypto] = useState('btc');
  const [showCryptoSelect, setShowCryptoSelect] = useState(false);
  
  const plan = PLANS[planId];
  
  const handleStripePayment = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API}/api/payments/stripe/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: originUrl
        })
      });
      
      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Error al iniciar el pago');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCryptoPayment = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API}/api/payments/crypto/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          plan_id: planId,
          pay_currency: selectedCrypto,
          origin_url: originUrl
        })
      });
      
      const data = await response.json();
      
      if (data.invoice_url) {
        window.location.href = data.invoice_url;
      } else {
        toast.error('Error al crear pago crypto');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };
  
  const selectedCryptoInfo = POPULAR_CRYPTOS.find(c => c.id === selectedCrypto) || POPULAR_CRYPTOS[0];
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-2xl w-full max-w-md" data-testid="payment-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h3 className="text-xl font-medium">Contratar plan</h3>
            <p className="text-sm text-neutral-500 mt-1">{plan?.name}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5 text-neutral-400" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {!paymentMethod && (
            <>
              {/* Price */}
              <div className="text-center mb-8 p-6 bg-black/30 rounded-xl">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-semibold">${plan?.price?.toLocaleString()}</span>
                  <span className="text-neutral-500">{plan?.currency} {plan?.period}</span>
                </div>
                <p className="text-sm text-neutral-500 mt-2">Pago anual</p>
              </div>
              
              {/* Payment Methods */}
              <div className="space-y-3">
                <p className="text-sm text-neutral-500 mb-4">Método de pago</p>
                
                <button
                  onClick={() => { setPaymentMethod('stripe'); handleStripePayment(); }}
                  disabled={loading}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-white/20 hover:bg-white/5 transition-all group"
                  data-testid="stripe-payment-btn"
                >
                  <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center group-hover:bg-white/10 transition-colors">
                    <CreditCard className="w-6 h-6 text-neutral-400" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">Tarjeta de Crédito</p>
                    <p className="text-sm text-neutral-500">Visa, Mastercard, Amex</p>
                  </div>
                </button>
                
                <button
                  onClick={() => setPaymentMethod('crypto')}
                  disabled={loading}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-white/20 hover:bg-white/5 transition-all group"
                  data-testid="crypto-payment-btn"
                >
                  <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center group-hover:bg-white/10 transition-colors">
                    <Wallet className="w-6 h-6 text-neutral-400" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">Criptomonedas</p>
                    <p className="text-sm text-neutral-500">BTC, ETH, USDT, SOL</p>
                  </div>
                </button>
              </div>
            </>
          )}
          
          {/* Crypto Selection */}
          {paymentMethod === 'crypto' && !loading && (
            <div>
              <p className="text-sm text-neutral-500 mb-4">Selecciona criptomoneda</p>
              
              <div className="relative mb-6">
                <button
                  onClick={() => setShowCryptoSelect(!showCryptoSelect)}
                  className="w-full flex items-center justify-between p-4 rounded-xl border border-white/20 hover:border-white/40 transition-colors"
                  data-testid="crypto-select-btn"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{selectedCryptoInfo.symbol}</span>
                    <span className="text-neutral-400">{selectedCryptoInfo.name}</span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${showCryptoSelect ? 'rotate-180' : ''}`} />
                </button>
                
                {showCryptoSelect && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-neutral-900 border border-white/20 rounded-xl overflow-hidden z-10">
                    {POPULAR_CRYPTOS.map((crypto) => (
                      <button
                        key={crypto.id}
                        onClick={() => {
                          setSelectedCrypto(crypto.id);
                          setShowCryptoSelect(false);
                        }}
                        className={`w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors ${
                          selectedCrypto === crypto.id ? 'bg-white/10' : ''
                        }`}
                      >
                        <span className="font-medium w-16 text-left">{crypto.symbol}</span>
                        <span className="text-neutral-400">{crypto.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="p-4 bg-black/30 rounded-xl mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-neutral-500">Plan</span>
                  <span>{plan?.name}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-neutral-500">Precio</span>
                  <span className="font-medium">${plan?.price} USD</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Pagar con</span>
                  <span>{selectedCryptoInfo.symbol}</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <Button
                  onClick={handleCryptoPayment}
                  disabled={loading}
                  className="w-full bg-white text-black hover:bg-neutral-200 rounded-full py-6 font-medium"
                  data-testid="confirm-crypto-btn"
                >
                  {loading ? 'Procesando...' : `Pagar con ${selectedCryptoInfo.symbol}`}
                </Button>
                
                <Button
                  onClick={() => setPaymentMethod(null)}
                  variant="outline"
                  className="w-full border-white/20 hover:bg-white/10 rounded-full"
                  data-testid="back-btn"
                >
                  Volver
                </Button>
              </div>
            </div>
          )}
          
          {loading && paymentMethod === 'stripe' && (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4" />
              <p className="text-neutral-500">Redirigiendo a Stripe...</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        {!loading && !paymentMethod && (
          <div className="p-6 border-t border-white/10">
            <p className="text-xs text-neutral-600 text-center">
              Pago seguro procesado por Stripe.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
